import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { onAuthStateChanged } from 'firebase/auth';
import { v4 as uuidv4 } from 'uuid';
import { auth } from '../../firebase';
import axios from 'utils/axios';
import { withRequestDedupe } from '../requestDedupe';
import { createContactThunk } from './contactsSlice';
import { createAuthedThunk } from '../authedThunk';

const initialState = {
  stakeholderLinkedinPosts: {
    fetching: null,
    fetchingSuccessful: null,
    fetchingFailed: null,
    error: null,
    errorText: null,
    linkedinPosts: {},
  },
  dealStakeholders: {
    fetching: null,
    fetchingSuccessful: null,
    fetchingFailed: null,
    error: null,
    errorText: null,
    stakeHolders: [],
  },
  companyLinkedinPosts: {
    fetching: null,
    fetchingSuccessful: null,
    fetchingFailed: null,
    data: null,
  },
  updateLinkedinData: {
    processing: null,
    done: null,
    failed: null,
    error: null,
  },
  addStakeholderFromContact: {
    processing: null,
    done: null,
    failed: null,
    error: null,
    errorText: null,
    data: null,
  },
  createContactAndAddAsStakeholder: {
    processing: null,
    done: null,
    failed: null,
    error: null,
    errorText: null,
    data: null,
  },
  updateDealStakeholder: {
    processing: null,
    done: null,
    failed: null,
    error: null,
    errorText: null,
    data: null,
  },
};

// Shared auth wrapper - see `redux/authedThunk`. Was a byte-identical
// local copy in each of eleven slices.
const thunk = createAuthedThunk;

export const fetchCompanyLinkedinPosts = thunk(
  'stakeholders/fetchCompanyLinkedinPosts',
  async (token, { companyId, generateNew }) => {
    const r = await axios.get(`/us/qai/companies/${companyId}/linkedIn/posts`, {
      params: { requestId: uuidv4(), generateNew },
      headers: { Authorization: `Bearer ${token}` },
    });
    if (r.status === 200) return r.data;
    throw new Error('Failed to fetch company LinkedIn posts');
  },
);

// Async variant (`regenerateV2`): returns immediately with an `asyncRequestId`
// to poll via `checkAsyncRequestStatus`, instead of blocking on the LLM call.
export const regenerateAboutIndustry = thunk(
  'stakeholders/regenerateAboutIndustry',
  async (token, { companyId }) => {
    const r = await axios.post(
      `/us/qai/companies/${companyId}/aboutIndustry/regenerateV2`,
      null,
      {
        params: { requestId: uuidv4() },
        headers: { Authorization: `Bearer ${token}` },
      },
    );
    if (r.status === 200) return r.data;
    throw new Error(r.data?.reason || 'Failed to regenerate about industry');
  },
);

export const generateSalesMotion = thunk(
  'stakeholders/generateSalesMotion',
  async (token, { companyId }) => {
    const r = await axios.post(
      `/us/qai/companies/${companyId}/salesMotion/generate`,
      null,
      {
        params: { requestId: uuidv4() },
        headers: { Authorization: `Bearer ${token}` },
      },
    );
    if (r.status === 200) return r.data;
    throw new Error(r.data?.reason || 'Failed to generate sales motion');
  },
);

export const checkAsyncRequestStatus = thunk(
  'stakeholders/checkAsyncRequestStatus',
  async (token, { asyncRequestId }) => {
    const r = await axios.get(`/us/dt/async-request/${asyncRequestId}/status`, {
      params: { requestId: uuidv4() },
      headers: { Authorization: `Bearer ${token}` },
    });
    if (r.status === 200) return r.data;
    throw new Error('Failed to check async request status');
  },
);

export const fetchDealCompanyById = thunk(
  'stakeholders/fetchDealCompanyById',
  async (token, { companyId }) => {
    const r = await axios.get(`/us/qai/companies/${companyId}`, {
      params: { requestId: uuidv4() },
      headers: { Authorization: `Bearer ${token}` },
    });
    if (r.status === 200) return r.data;
    throw new Error('Failed to fetch company');
  },
);

export const scrapeLinkedinData = createAsyncThunk(
  'stakeholders/scrapeLinkedinData',
  async (lnUrl, { rejectWithValue }) => {
    return new Promise((resolve, reject) => {
      const unsubscribe = onAuthStateChanged(
        auth,
        async (user) => {
          unsubscribe();
          try {
            const token = await user?.getIdToken();
            const response = await axios.post(
              '/us/qai/stake-holders/linkedin/scrape',
              { url: lnUrl },
              {
                params: {
                  requestId: uuidv4(),
                },
                headers: token ? { Authorization: `Bearer ${token}` } : {},
              },
            );

            if (response.status === 200 && response.data) {
              resolve(response.data);
            } else {
              reject(rejectWithValue('Failed to scrape LinkedIn data'));
            }
          } catch (e) {
            console.error('LinkedIn extraction failed:', e);
            reject(rejectWithValue(e.message));
          }
        },
        (error) => {
          reject(rejectWithValue(error.message));
        },
      );
    });
  },
);

export const fetchStakeholderLinkedInPosts = createAsyncThunk(
  'stakeholders/fetchStakeholderLinkedInPosts',
  async ({ contactId, generateNew }, { dispatch, rejectWithValue }) => {
    return new Promise((resolve, reject) => {
      const unsubscribe = onAuthStateChanged(
        auth,
        async (user) => {
          unsubscribe();
          try {
            const token = await user?.getIdToken();
            const reqId = uuidv4();
            const response = await axios.get(
              `/us/contacts/${contactId}/linkedin/posts`,
              {
                params: {
                  requestId: reqId,
                  generateNew: generateNew,
                },
                headers: token ? { Authorization: `Bearer ${token}` } : {},
              },
            );

            if (response.status === 200) {
              resolve({ contactId, data: response.data });
            } else {
              reject(
                rejectWithValue('Failed to get linkedin posts for stakeholder'),
              );
            }
          } catch (e) {
            reject(
              rejectWithValue('Failed to get linkedin posts for stakeholder'),
            );
          }
        },
        (error) => {
          reject(rejectWithValue(error.message));
        },
      );
    });
  },
);

export const fetchDealStakeholders = createAsyncThunk(
  'stakeholders/fetchDealStakeholders',
  // Five components on the deal page ask for the stakeholder list on mount;
  // they share one request. See `redux/requestDedupe`.
  withRequestDedupe(
    (arg) => (arg?.dealId ? `dealStakeholders:${arg.dealId}` : null),
    async ({ dealId }, { dispatch, rejectWithValue }) => {
      return new Promise((resolve, reject) => {
        const unsubscribe = onAuthStateChanged(
          auth,
          async (user) => {
            unsubscribe();
            try {
              const token = await user?.getIdToken();
              const reqId = uuidv4();
              const response = await axios.get(
                `/us/qai/deals/${dealId}/stake-holders`,
                {
                  params: {
                    'request-id': reqId,
                  },
                  headers: token ? { Authorization: `Bearer ${token}` } : {},
                },
              );

              if (response.status === 200) {
                resolve(response.data);
              } else {
                reject(rejectWithValue('Failed to get deal stakeholders'));
              }
            } catch (e) {
              reject(rejectWithValue('Failed to get deal stakeholders'));
            }
          },
          (error) => {
            reject(rejectWithValue(error.message));
          },
        );
      });
    },
  ),
);

export const addStakeholderFromContactThunk = thunk(
  'stakeholders/addStakeholderFromContact',
  async (
    token,
    { dealId, contactId, labels, contactRole, source, version },
  ) => {
    const r = await axios.post(
      `/us/qai/deals/${dealId}/stake-holder/contact`,
      { contactId, labels, contactRole, source: source || 'manual' },
      {
        params: { 'request-id': uuidv4() },
        headers: {
          Authorization: `Bearer ${token}`,
          'x-api-version': version || 'v1',
        },
      },
    );
    if (r.status === 200) return r.data;
    throw new Error('Failed to associate contact as stakeholder');
  },
);

// Edits an existing stakeholder in place. Previously the org chart changed a
// stakeholder's labels by deleting the row and re-adding the same contact,
// which churned the stakeholder id (and everything keyed off it).
export const updateDealStakeholderThunk = thunk(
  'stakeholders/updateDealStakeholder',
  async (token, { dealId, stakeholderId, labels, contactRole }) => {
    const r = await axios.put(
      `/us/qai/deals/${dealId}/stake-holder/${stakeholderId}`,
      { labels, ...(contactRole ? { contactRole } : {}) },
      {
        params: { 'request-id': uuidv4() },
        headers: { Authorization: `Bearer ${token}` },
      },
    );
    if (r.status === 200) return r.data;
    throw new Error('Failed to update stakeholder');
  },
);

export const createContactAndAddAsStakeholderThunk = createAsyncThunk(
  'stakeholders/createContactAndAddAsStakeholder',
  async ({ dealId, payload, labels }, { dispatch, rejectWithValue }) => {
    try {
      const { contactData, stackHolderData, asyncRequestId } = await dispatch(
        createContactThunk({
          name: payload?.name,
          email: payload?.email,
          emails: payload?.emails || [],
          altEmail: payload?.altEmail,
          phoneNumber: payload?.phoneNumber,
          phoneNumbers: payload?.phoneNumbers || [],
          linkedIn: payload?.linkedIn,
          contactRole: payload?.contactRole || 'EMPLOYEE',
          companyId: payload?.companyId,
          verified: payload?.verified,
          verifiedByName: payload?.verifiedByName,
          dealId,
          labels,
        }),
      ).unwrap();

      return {
        contactData: [contactData],
        stackHolderData: [stackHolderData],
        asyncRequestId,
      };
    } catch (e) {
      return rejectWithValue(e);
    }
  },
);

const stakeholdersSlice = createSlice({
  name: 'stakeholders',
  initialState,
  reducers: {
    resetDealStakeholders: (state) => {
      state.dealStakeholders = {
        fetching: null,
        fetchingSuccessful: null,
        fetchingFailed: null,
        error: null,
        errorText: null,
        stakeHolders: [],
      };
    },
    resetStakeholderLinkedInPosts: (state) => {
      state.stakeholderLinkedinPosts = {
        fetching: null,
        fetchingSuccessful: null,
        fetchingFailed: null,
        error: null,
        errorText: null,
        linkedinPosts: {},
      };
    },

    setDealStakeholders: (state, action) => {
      state.dealStakeholders.stakeHolders = action.payload;
    },

    addOptimisticStakeholders: (state, action) => {
      const { stakeholders, processing } = action.payload || {};
      const existingIds = new Set(
        state.dealStakeholders.stakeHolders.map((s) => s?.id),
      );
      const newOnes = (stakeholders || [])
        .filter((s) => !existingIds.has(s?.id))
        .map((s) => ({ ...s, _processing: Boolean(processing) }));
      state.dealStakeholders.stakeHolders = [
        ...newOnes,
        ...state.dealStakeholders.stakeHolders,
      ];
    },

    clearStakeholderProcessing: (state, action) => {
      state.dealStakeholders.stakeHolders = state.dealStakeholders.stakeHolders.map(
        (s) => (s?.id === action.payload ? { ...s, _processing: false } : s),
      );
    },
  },
  extraReducers: (builder) => {
    // --- Deal Stakeholders ---
    builder
      .addCase(fetchDealStakeholders.pending, (state) => {
        state.dealStakeholders.fetching = true;
      })
      .addCase(fetchDealStakeholders.fulfilled, (state, action) => {
        state.dealStakeholders.fetching = false;
        state.dealStakeholders.fetchingSuccessful = true;
        state.dealStakeholders.fetchingFailed = false;
        state.dealStakeholders.error = false;
        state.dealStakeholders.errorText = '';
        state.dealStakeholders.stakeHolders = action.payload;
      })
      .addCase(fetchDealStakeholders.rejected, (state, action) => {
        state.dealStakeholders.fetching = false;
        state.dealStakeholders.fetchingFailed = true;
        state.dealStakeholders.error = true;
        state.dealStakeholders.errorText = action.payload || 'Failed';
        state.dealStakeholders.stakeHolders = [];
      });

    builder
      .addCase(fetchCompanyLinkedinPosts.pending, (s) => {
        s.companyLinkedinPosts.fetching = true;
        s.companyLinkedinPosts.fetchingSuccessful = false;
        s.companyLinkedinPosts.fetchingFailed = false;
      })
      .addCase(fetchCompanyLinkedinPosts.fulfilled, (s, a) => {
        s.companyLinkedinPosts.fetching = false;
        s.companyLinkedinPosts.fetchingSuccessful = true;
        s.companyLinkedinPosts.fetchingFailed = false;
        s.companyLinkedinPosts.data = a.payload;
      })
      .addCase(fetchCompanyLinkedinPosts.rejected, (s) => {
        s.companyLinkedinPosts.fetching = false;
        s.companyLinkedinPosts.fetchingSuccessful = false;
        s.companyLinkedinPosts.fetchingFailed = true;
      });

    builder
      .addCase(fetchStakeholderLinkedInPosts.pending, (state) => {
        state.stakeholderLinkedinPosts.fetching = true;
      })
      .addCase(fetchStakeholderLinkedInPosts.fulfilled, (state, action) => {
        state.stakeholderLinkedinPosts.fetching = false;
        state.stakeholderLinkedinPosts.fetchingSuccessful = true;
        state.stakeholderLinkedinPosts.fetchingFailed = false;
        state.stakeholderLinkedinPosts.error = false;
        state.stakeholderLinkedinPosts.errorText = '';
        state.stakeholderLinkedinPosts.linkedinPosts = {
          ...state.stakeholderLinkedinPosts.linkedinPosts,
          [action.payload.contactId]: action.payload.data,
        };
      })
      .addCase(fetchStakeholderLinkedInPosts.rejected, (state, action) => {
        state.stakeholderLinkedinPosts.fetching = false;
        state.stakeholderLinkedinPosts.fetchingFailed = true;
        state.stakeholderLinkedinPosts.error = true;
        state.stakeholderLinkedinPosts.errorText = action.payload || 'Failed';
      });

    builder
      .addCase(addStakeholderFromContactThunk.pending, (s) => {
        s.addStakeholderFromContact.processing = true;
        s.addStakeholderFromContact.done = false;
        s.addStakeholderFromContact.failed = false;
      })
      .addCase(addStakeholderFromContactThunk.fulfilled, (s, a) => {
        s.addStakeholderFromContact.processing = false;
        s.addStakeholderFromContact.done = true;
        s.addStakeholderFromContact.failed = false;
        s.addStakeholderFromContact.data = a.payload;
      })
      .addCase(addStakeholderFromContactThunk.rejected, (s, a) => {
        s.addStakeholderFromContact.processing = false;
        s.addStakeholderFromContact.done = false;
        s.addStakeholderFromContact.failed = true;
        s.addStakeholderFromContact.error = a.payload;
        s.addStakeholderFromContact.errorText =
          typeof a.payload === 'string' ? a.payload : a.payload?.message;
      });

    builder
      .addCase(createContactAndAddAsStakeholderThunk.pending, (s) => {
        s.createContactAndAddAsStakeholder.processing = true;
        s.createContactAndAddAsStakeholder.done = false;
        s.createContactAndAddAsStakeholder.failed = false;
      })
      .addCase(createContactAndAddAsStakeholderThunk.fulfilled, (s, a) => {
        s.createContactAndAddAsStakeholder.processing = false;
        s.createContactAndAddAsStakeholder.done = true;
        s.createContactAndAddAsStakeholder.failed = false;
        s.createContactAndAddAsStakeholder.data = a.payload;
      })
      .addCase(createContactAndAddAsStakeholderThunk.rejected, (s, a) => {
        s.createContactAndAddAsStakeholder.processing = false;
        s.createContactAndAddAsStakeholder.done = false;
        s.createContactAndAddAsStakeholder.failed = true;
        s.createContactAndAddAsStakeholder.error = a.payload;
        s.createContactAndAddAsStakeholder.errorText =
          typeof a.payload === 'string' ? a.payload : a.payload?.message;
      });

    builder
      .addCase(updateDealStakeholderThunk.pending, (s) => {
        s.updateDealStakeholder.processing = true;
        s.updateDealStakeholder.done = false;
        s.updateDealStakeholder.failed = false;
      })
      .addCase(updateDealStakeholderThunk.fulfilled, (s, a) => {
        s.updateDealStakeholder.processing = false;
        s.updateDealStakeholder.done = true;
        s.updateDealStakeholder.failed = false;
        s.updateDealStakeholder.data = a.payload;

        const { stakeholderId, labels } = a.meta.arg || {};
        s.dealStakeholders.stakeHolders = s.dealStakeholders.stakeHolders?.map(
          (sth) => (sth?.id === stakeholderId ? { ...sth, labels } : sth),
        );
      })
      .addCase(updateDealStakeholderThunk.rejected, (s, a) => {
        s.updateDealStakeholder.processing = false;
        s.updateDealStakeholder.done = false;
        s.updateDealStakeholder.failed = true;
        s.updateDealStakeholder.error = a.payload;
        s.updateDealStakeholder.errorText =
          typeof a.payload === 'string' ? a.payload : a.payload?.message;
      });
  },
});

export const {
  resetDealStakeholders,
  resetStakeholderLinkedInPosts,
  setDealStakeholders,
  addOptimisticStakeholders,
  clearStakeholderProcessing,
} = stakeholdersSlice.actions;

export default stakeholdersSlice.reducer;
